import { useState, useEffect } from "react";
import { differenceInDays } from "date-fns";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Sparkles, Rocket, X, Loader2, ArrowRight } from "lucide-react";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import i18n from "@/i18n/config";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  formatDiscountMessage,
  saveCouponData,
  clearCouponData,
  getCouponData,
  getFormattedProductList,
  validateCouponGlobally,
  hasValidCoupons,
  CouponResult,
} from "@/lib/couponUtils";

interface UpsellPackage {
  id: "spotlight" | "social_boost";
  icon: typeof Sparkles;
  price: number;
  duration: string;
  bgColor: string;
  borderColor: string;
}

const packages: UpsellPackage[] = [
  {
    id: "spotlight",
    icon: Sparkles,
    price: 149,
    duration: "28",
    bgColor: "bg-amber-400",
    borderColor: "border-amber-600",
  },
  {
    id: "social_boost",
    icon: Rocket,
    price: 399,
    duration: "28",
    bgColor: "bg-violet-400",
    borderColor: "border-violet-600",
  },
];

export const UpsellBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isSubscriber } = useAuth();
  const [hasActiveSpotlight, setHasActiveSpotlight] = useState<boolean | null>(null);
  const [hasActiveSocialBoost, setHasActiveSocialBoost] = useState<boolean | null>(null);
  const [spotlightEndDate, setSpotlightEndDate] = useState<Date | null>(null);
  const [socialBoostEndDate, setSocialBoostEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<UpsellPackage | null>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupons, setAppliedCoupons] = useState<Record<string, CouponResult | null>>({});
  const [isValidating, setIsValidating] = useState(false);

  const hasAnyCouponApplied = Object.values(appliedCoupons).some(c => c?.valid);

  // Load coupon from sessionStorage on mount
  useEffect(() => {
    const { code, data } = getCouponData();
    if (code && data) {
      setCouponCode(code);
      // Filter only relevant coupons for this component
      const relevantCoupons: Record<string, CouponResult | null> = {};
      if (data['spotlight']) relevantCoupons['spotlight'] = data['spotlight'];
      if (data['social_boost']) relevantCoupons['social_boost'] = data['social_boost'];
      if (Object.keys(relevantCoupons).length > 0) {
        setAppliedCoupons(relevantCoupons);
      }
    }
  }, []);

  useEffect(() => {
    if (user && isSubscriber()) {
      checkActivePackages();
    } else {
      setLoading(false);
    }
  }, [user]);

  const checkActivePackages = async () => {
    try {
      // Check for active spotlight with end_date
      const { data: spotlight } = await supabase
        .from("featured_professionals")
        .select("id, end_date")
        .eq("user_id", user?.id)
        .eq("is_active", true)
        .eq("feature_type", "spotlight")
        .gte("end_date", new Date().toISOString())
        .maybeSingle();

      setHasActiveSpotlight(!!spotlight);
      setSpotlightEndDate(spotlight?.end_date ? new Date(spotlight.end_date) : null);

      // Check for active social_boost subscription with current_period_end
      const { data: socialBoost } = await supabase
        .from("subscriptions")
        .select("id, current_period_end")
        .eq("user_id", user?.id)
        .eq("product_type", "social_boost")
        .eq("status", "active")
        .gte("current_period_end", new Date().toISOString())
        .maybeSingle();

      setHasActiveSocialBoost(!!socialBoost);
      setSocialBoostEndDate(socialBoost?.current_period_end ? new Date(socialBoost.current_period_end) : null);
    } catch (error) {
      console.error("Error checking active packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateCouponCode = async () => {
    if (!couponCode.trim()) {
      toast({
        title: t('pricing.coupon.error'),
        description: t('pricing.coupon.emptyCode'),
        variant: "error",
      });
      return;
    }

    setIsValidating(true);
    try {
      // Validate against ALL product types globally
      const allResults = await validateCouponGlobally(supabase, couponCode.trim().toUpperCase());
      
      // Check if any product is valid globally
      const hasAnyValid = hasValidCoupons(allResults);
      
      if (hasAnyValid) {
        // Save ALL results to sessionStorage
        saveCouponData(couponCode.trim().toUpperCase(), allResults);
        setAppliedCoupons(allResults);
        
        const locale = i18n.language || 'it';
        const formattedProducts = getFormattedProductList(allResults, locale);
        const sample = Object.values(allResults).find(r => r?.valid);
        
        toast({
          title: t('pricing.coupon.success'),
          description: t('pricing.coupon.validDynamic', { 
            products: formattedProducts,
            discount: formatDiscountMessage(sample?.discountType || 'percentage', sample?.discountValue || 0)
          }),
        });
      } else {
        setAppliedCoupons({});
        toast({
          title: t('pricing.coupon.error'),
          description: t('pricing.coupon.invalid'),
          variant: "error",
        });
      }
    } catch (error: any) {
      console.error("Coupon validation error:", error);
      toast({
        title: t('pricing.coupon.error'),
        description: error.message || t('pricing.coupon.validationError'),
        variant: "error",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode("");
    setAppliedCoupons({});
    clearCouponData();
    toast({
      title: t('pricing.coupon.removed'),
      description: t('pricing.coupon.removedDescription'),
    });
  };

  const calculateFinalPrice = (pkgId: string, originalPrice: number): number => {
    const coupon = appliedCoupons[pkgId];
    if (!coupon?.valid) return originalPrice;
    if (coupon.discountType === 'full') return 0;
    if (coupon.discountType === 'fixed') return Math.max(0, originalPrice - coupon.discountValue);
    return originalPrice * (1 - coupon.discountPercent / 100);
  };

  const handleCheckout = async (pkg: UpsellPackage) => {
    setIsCheckingOut(true);
    try {
      const couponForPlan = appliedCoupons[pkg.id]?.valid ? couponCode.toUpperCase() : undefined;

      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          planType: pkg.id,
          billingType: "one_time_28_days",
          couponCode: couponForPlan,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: t("dashboard.upsell.activated"),
          description: data.message,
        });
        setSelectedPackage(null);
        checkActivePackages();
      } else if (data.url) {
        window.open(data.url, "_blank");
        setSelectedPackage(null);
      }
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message || t("dashboard.upsell.checkoutError"),
        variant: "error",
      });
    } finally {
      setIsCheckingOut(false);
    }
  };

  // Don't render if still loading
  if (loading || hasActiveSpotlight === null || hasActiveSocialBoost === null) {
    return null;
  }

  return (
    <>
      <div className="mb-8 p-6 bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/10 border-[3px] border-dark shadow-[4px_4px_0px_#101322]">
        <div className="text-center mb-6">
          <h3 className="font-heading font-bold text-xl md:text-2xl text-dark mb-2">
            {t("dashboard.upsell.title")}
          </h3>
          <p className="text-muted-foreground text-sm md:text-base">
            {t("dashboard.upsell.subtitle")}
          </p>
        </div>

        {/* Coupon Form */}
        <div className="max-w-lg mx-auto mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <BrutalInput
              placeholder={t('pricing.coupon.placeholder')}
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              className="flex-1"
              disabled={hasAnyCouponApplied}
            />
            {!hasAnyCouponApplied ? (
              <BrutalButton 
                onClick={validateCouponCode} 
                disabled={isValidating || !couponCode.trim()}
                size="sm"
                className="whitespace-nowrap"
              >
                {isValidating ? t('pricing.coupon.validating') : t('pricing.coupon.apply')}
              </BrutalButton>
            ) : (
              <BrutalButton 
                onClick={removeCoupon} 
                variant="outline"
                size="sm"
                className="whitespace-nowrap flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                {t('pricing.coupon.remove')}
              </BrutalButton>
            )}
          </div>

          {/* Coupon confirmation box */}
          {hasAnyCouponApplied && (() => {
            const sample = Object.values(appliedCoupons).find(c => c?.valid);
            if (!sample) return null;
            const discount = formatDiscountMessage(sample.discountType, sample.discountValue);
            const locale = i18n.language || 'it';
            const formattedProducts = getFormattedProductList(appliedCoupons, locale);
            return (
              <div className="mt-3 p-3 bg-primary/10 border-[3px] border-primary">
                <p className="font-heading font-bold text-dark text-sm">
                  {t('pricing.coupon.couponConfirmation', { discount })}
                </p>
                <p className="font-body text-xs text-muted-foreground mt-1">
                  {t('pricing.coupon.appliesToFormatted', { products: formattedProducts })}
                </p>
              </div>
            );
          })()}
        </div>

        <div className={`grid gap-4 ${packages.length === 1 ? "max-w-md mx-auto" : "md:grid-cols-2"}`}>
          {packages.map((pkg) => {
            const Icon = pkg.icon;
            const isActive = (pkg.id === "spotlight" && hasActiveSpotlight) 
                          || (pkg.id === "social_boost" && hasActiveSocialBoost);
            const endDate = pkg.id === "spotlight" ? spotlightEndDate : socialBoostEndDate;
            const finalPrice = calculateFinalPrice(pkg.id, pkg.price);
            const hasDiscount = !isActive && appliedCoupons[pkg.id]?.valid && finalPrice !== pkg.price;
            const daysRemaining = endDate 
              ? differenceInDays(endDate, new Date())
              : 0;

            // Mutual exclusion logic
            const isOtherPackageActive = pkg.id === "spotlight" 
              ? hasActiveSocialBoost 
              : hasActiveSpotlight;
            const otherPackageName = pkg.id === "spotlight" 
              ? t("dashboard.upsell.packages.social_boost.name")
              : t("dashboard.upsell.packages.spotlight.name");
            const otherPackageEndDate = pkg.id === "spotlight" 
              ? socialBoostEndDate 
              : spotlightEndDate;
            const otherDaysRemaining = otherPackageEndDate 
              ? differenceInDays(otherPackageEndDate, new Date())
              : 0;

            const isDisabled = isActive || isOtherPackageActive;

            return (
              <div
                key={pkg.id}
                className={`bg-white border-[3px] ${isActive ? 'border-primary' : isOtherPackageActive ? 'border-muted opacity-60' : 'border-dark'} p-5 ${!isDisabled ? 'hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#101322] cursor-pointer' : ''} transition-all group`}
                onClick={() => !isDisabled && setSelectedPackage(pkg)}
              >
                <div className="flex items-start gap-4">
                <div className={`w-12 h-12 ${pkg.bgColor} ${pkg.borderColor} border-[3px] flex items-center justify-center ${isOtherPackageActive ? 'opacity-50' : ''}`}>
                  <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-heading font-bold text-lg text-dark mb-1">
                      {t(`dashboard.upsell.packages.${pkg.id}.name`)}
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      {t(`dashboard.upsell.packages.${pkg.id}.description`)}
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        {isActive ? (
                          <span className="font-heading font-bold text-lg text-primary">
                            {t("dashboard.upsell.activeFor", { days: daysRemaining })}
                          </span>
                        ) : isOtherPackageActive ? (
                          <span className="font-heading font-bold text-sm text-muted-foreground">
                            {t("dashboard.upsell.otherPackageActiveFor", { package: otherPackageName, days: otherDaysRemaining })}
                          </span>
                        ) : hasDiscount ? (
                          <>
                            <span className="font-heading font-bold text-lg text-muted-foreground line-through mr-2">
                              €{pkg.price}
                            </span>
                            <span className="font-heading font-bold text-2xl text-primary">
                              {finalPrice === 0 ? t('common.free') : `€${finalPrice.toFixed(0)}`}
                            </span>
                          </>
                        ) : (
                          <span className="font-heading font-bold text-2xl text-dark">€{pkg.price}</span>
                        )}
                        {!isActive && !isOtherPackageActive && (
                          <span className="text-sm text-muted-foreground ml-1">
                            / {pkg.duration} {t("dashboard.upsell.days")}
                          </span>
                        )}
                      </div>
                      {isActive ? (
                        <span className="bg-primary text-primary-foreground font-heading font-bold text-xs uppercase px-3 py-1 border-[2px] border-dark">
                          {t("dashboard.upsell.active")}
                        </span>
                      ) : isOtherPackageActive ? (
                        <span className="bg-muted text-muted-foreground font-heading font-bold text-xs uppercase px-3 py-1 border-[2px] border-muted">
                          {t("dashboard.upsell.notAvailable")}
                        </span>
                      ) : (
                        <BrutalButton size="sm" className="group-hover:bg-primary/90">
                          {t("dashboard.upsell.buy")}
                        </BrutalButton>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* See All Features Link */}
        <div className="text-center mt-4">
          <button
            onClick={() => navigate("/visibility-packages")}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-dark underline underline-offset-4 transition-colors"
          >
            {t("dashboard.upsell.seeAllFeatures")}
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog open={!!selectedPackage} onOpenChange={() => setSelectedPackage(null)}>
        <DialogContent className="border-[3px] border-dark shadow-[6px_6px_0px_#101322]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedPackage && t(`dashboard.upsell.packages.${selectedPackage.id}.name`)}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {selectedPackage && t(`dashboard.upsell.modal.description`, {
                name: t(`dashboard.upsell.packages.${selectedPackage.id}.name`),
                price: selectedPackage.price,
                days: selectedPackage.duration,
              })}
            </DialogDescription>
          </DialogHeader>

          {selectedPackage && (
            <div className="py-4">
              {/* Show price with discount in modal */}
              {(() => {
                const finalPrice = calculateFinalPrice(selectedPackage.id, selectedPackage.price);
                const hasDiscount = appliedCoupons[selectedPackage.id]?.valid && finalPrice !== selectedPackage.price;
                if (hasDiscount) {
                  return (
                    <div className="mb-4 p-3 bg-primary/10 border-[2px] border-primary text-center">
                      <span className="font-heading font-bold text-lg text-muted-foreground line-through mr-2">
                        €{selectedPackage.price}
                      </span>
                      <span className="font-heading font-bold text-2xl text-primary">
                        {finalPrice === 0 ? t('common.free') : `€${finalPrice.toFixed(0)}`}
                      </span>
                    </div>
                  );
                }
                return null;
              })()}

              <ul className="space-y-2">
                {(t(`dashboard.upsell.packages.${selectedPackage.id}.benefits`, { returnObjects: true }) as string[]).map(
                  (benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <span className="text-primary">✓</span>
                      {benefit}
                    </li>
                  )
                )}
              </ul>
            </div>
          )}

          <DialogFooter className="gap-2">
            <BrutalButton variant="outline" onClick={() => setSelectedPackage(null)} disabled={isCheckingOut}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              onClick={() => selectedPackage && handleCheckout(selectedPackage)}
              disabled={isCheckingOut}
              className="gap-2"
            >
              {isCheckingOut ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("dashboard.upsell.processing")}
                </>
              ) : (
                <>
                  {(() => {
                    if (!selectedPackage) return t("dashboard.upsell.confirmBuy", { price: 0 });
                    const finalPrice = calculateFinalPrice(selectedPackage.id, selectedPackage.price);
                    return finalPrice === 0 
                      ? t("dashboard.upsell.activateNow") 
                      : t("dashboard.upsell.confirmBuy", { price: finalPrice.toFixed(0) });
                  })()}
                </>
              )}
            </BrutalButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
